import type { FC } from 'react';

import { Box, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { Country } from 'src/api/zrm';
import { formatCount, formatKr, formatMillions, formatShortVolume, SHORT_MILLION } from 'src/tv/dashboardFormat';
import { Figure, Kicker, monthName, NumCell, podiumRowClass, RankChip, tableSx } from 'src/tv/dashboardParts';
import type { KhDashboard as KhDashboardData, KhTotals, TvDashboardSnapshot } from 'src/tv/dashboardTypes';
import { khView } from 'src/tv/dashboardView';
import { TV_COLORS, TV_PODIUM } from 'src/tv/tvTheme';

interface PeriodTotalsProps {
  label: string;
  totals: KhTotals;
  country: Country;
}

const PeriodTotals: FC<PeriodTotalsProps> = (props) => {
  const { label, totals, country } = props;
  const { t } = useTranslation();

  return (
    <Box sx={{ borderTop: `1px solid ${TV_COLORS.line}`, pt: '1.4vh' }}>
      <Kicker>{label}</Kicker>
      {/* Two by two: TF and LF carry their monthly premium under the count, which four columns abreast cannot fit. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '1.2vw', rowGap: '0.6vh', mt: '1vh' }}>
        <Figure
          label={t('Sent')}
          value={formatCount(totals.utskick)}
          detail={formatShortVolume(totals.utskick_volume, country)}
        />
        <Figure
          label={t('Helped')}
          value={formatCount(totals.helped)}
          detail={formatShortVolume(totals.helped_volume, country)}
        />
        <Figure
          label={t('TF')}
          value={formatCount(totals.tf)}
          detail={t('{{amount}}/mo', { amount: formatKr(totals.tf_premium) })}
        />
        <Figure
          label={t('LF')}
          value={formatCount(totals.lf)}
          detail={t('{{amount}}/mo', { amount: formatKr(totals.lf_premium) })}
        />
      </Box>
    </Box>
  );
};

interface HandlerTableProps {
  view: ReturnType<typeof khView>;
  month: string;
  country: Country;
}

// Volumes in millions: 18 period columns only fit 1920 px without the full `1 870 000 kr` form.
const periodCells = (row: KhTotals) => [
  { value: row.utskick, text: formatCount(row.utskick), className: 'group-start' },
  { value: row.utskick_volume, text: formatMillions(row.utskick_volume) },
  { value: row.helped, text: formatCount(row.helped), className: 'strong' },
  { value: row.helped_volume, text: formatMillions(row.helped_volume), className: 'strong' },
  { value: row.tf, text: formatCount(row.tf) },
  { value: row.lf, text: formatCount(row.lf) },
];

/** Ranked by this month's helped; the top three on the podium colours (the KH team is about a dozen). */
const KH_PODIUM = 3;

const HandlerTable: FC<HandlerTableProps> = (props) => {
  const { view, month, country } = props;
  const { t } = useTranslation();
  const { handlers, totals } = view;
  // The unit alone heads each volume column; `Volume (MNOK)` would be wider than the numbers under it.
  const volume = SHORT_MILLION[country];

  if (!handlers.length)
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: `1px solid ${TV_COLORS.line}`, mt: '3vh' }}>
        <Typography sx={{ fontSize: '1.6vw', color: TV_COLORS.muted }}>
          {t('Nothing sent or helped this month yet.')}
        </Typography>
      </Box>
    );

  return (
    <Box sx={{ flex: 1, minHeight: 0, mt: '2.4vh', display: 'flex', flexDirection: 'column' }}>
      <Table
        size="small"
        data-testid="tv-kh-handlers"
        sx={tableSx('1.2vw')}
      >
        <TableHead>
          <TableRow>
            <TableCell colSpan={2}>{t('Credit handlers')}</TableCell>
            <TableCell
              colSpan={6}
              className="group-start"
            >
              {t('Today')}
            </TableCell>
            <TableCell
              colSpan={6}
              className="group-start"
            >
              {t('This week')}
            </TableCell>
            <TableCell
              colSpan={6}
              className="group-start"
            >
              {month}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="rank" />
            <TableCell className="name" />
            {[0, 1, 2].map((group) => [
              <TableCell
                key={`${group}s`}
                className="group-start"
              >
                {t('Sent')}
              </TableCell>,
              <TableCell key={`${group}sv`}>{volume}</TableCell>,
              <TableCell key={`${group}h`}>{t('Helped')}</TableCell>,
              <TableCell key={`${group}hv`}>{volume}</TableCell>,
              <TableCell key={`${group}t`}>{t('TF')}</TableCell>,
              <TableCell key={`${group}l`}>{t('LF')}</TableCell>,
            ])}
          </TableRow>
        </TableHead>
        <TableBody>
          {handlers.map((handler, index) => (
            <TableRow
              key={handler.user_id}
              className={podiumRowClass(index, KH_PODIUM, handlers.length)}
            >
              <TableCell className="rank">
                <RankChip
                  rank={index + 1}
                  podium={TV_PODIUM.slice(0, KH_PODIUM)}
                />
              </TableCell>
              <TableCell className="name">{handler.name}</TableCell>
              {[...periodCells(handler.today), ...periodCells(handler.week), ...periodCells(handler.month)].map((cell, column) => (
                <NumCell
                  key={column}
                  {...cell}
                />
              ))}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell className="rank" />
            {/* zrm counts every accept and disbursal in the totals but gives rows to employees only, so the
                footer can be more than the rows add up to; it says so rather than read as a column sum. */}
            <TableCell className="name">
              {t('Team total')}
              <Box
                component="span"
                sx={{ display: 'block', whiteSpace: 'normal', fontSize: '0.7em', fontWeight: 500, color: TV_COLORS.muted }}
              >
                {view.hiddenHandlers
                  ? t('Including unassigned and {{count}} more not shown', { count: view.hiddenHandlers })
                  : t('Including unassigned')}
              </Box>
            </TableCell>
            {[...periodCells(totals.today), ...periodCells(totals.week), ...periodCells(totals.month)].map((cell, column) => (
              <TableCell
                key={column}
                className={cell.className}
              >
                {cell.text}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      </Table>
    </Box>
  );
};

interface KhDashboardProps {
  snapshot: TvDashboardSnapshot;
  kh: KhDashboardData;
}

/**
 * The credit handlers' screen (replaces "Stats KH NO"). One screen, no rotation: the team totals for
 * today, the week and the month across the top, then one leaderboard ranked by the month, with
 * the same six columns (sent, volume, helped, volume, TF, LF) for today, the week and the month. Everything stays in
 * view, so nobody waits for a slide to come round.
 */
const KhDashboard: FC<KhDashboardProps> = (props) => {
  const { snapshot, kh } = props;
  const { t } = useTranslation();

  const view = khView(kh);
  const { country } = snapshot;
  const month = monthName(snapshot.today, country);
  const periods = [
    { key: 'today', label: t('Today'), totals: view.totals.today },
    { key: 'week', label: t('This week'), totals: view.totals.week },
    { key: 'month', label: month, totals: view.totals.month },
  ];

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', mt: '2.2vh' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.25fr', columnGap: '3.2vw' }}>
        {periods.map((period) => (
          <PeriodTotals
            key={period.key}
            label={period.label}
            totals={period.totals}
            country={country}
          />
        ))}
      </Box>

      <HandlerTable
        view={view}
        month={month}
        country={country}
      />
    </Box>
  );
};

export default KhDashboard;
